// Depth-limited minimax with alpha-beta pruning + PIMC determinization.
//   - profile.determinization.samples ≥ 2 enables Perfect-Information
//     Monte Carlo: at the root, sample N possible shuffles of the
//     opponent's hand+deck pool, run a full minimax in each "world",
//     and average per-action scores. The action that's best on average
//     across plausible opponent hands is the AI's pick.
//   - samples=1 (or undefined) skips determinization entirely and uses
//     the live state with classic root α-β pruning — equivalent to the
//     pre-determinization skeleton.
//   - Within each determinization, both sides see the sampled world
//     fully (PIMC's "strategy fusion" simplification). A future
//     follow-up could swap to asymmetric belief modelling where the
//     opponent's nodes use their own determinizations.
//
// The opponent's deck composition is treated as public knowledge —
// reasonable for Cardverse since both players bring named decks. What
// determinization actually randomises is the hand/deck split: which of
// the opponent's unrevealed cards are currently in hand vs. on top of
// their deck. The AI's decisions, averaged across samples, no longer
// depend on the specific ordering the engine happens to have.

import type { CardDef, GameAction, GameState, PlayerSide, PRNGState } from '../types.js';
import { getLegalActions, canSupport, mustDiscard, defenderHasEligibleBlocker } from '../engine/Rules.js';
import { replayActions } from '../engine/Replay.js';
import { cloneState, getOpponentSide, getPlayerState, getEffectivePower } from '../engine/GameState.js';
import { canAfford } from '../engine/SpiritManager.js';
import { getRalliesPerTurn, getSupportsPerTurn } from '../engine/AbilityProcessor.js';
import { clonePRNG, nextFloat, nextInt, shuffleWith } from '../engine/PRNG.js';
import { evaluate } from './Evaluator.js';
import type { AIProfile, ScoredAction, SearchReport } from './types.js';

/** Whose move is it at this state. In the counter phase, the defender
 *  (opposite of currentPlayer) acts; everywhere else it's currentPlayer. */
function actingSide(state: GameState): PlayerSide {
  return state.phase === 'counter' ? getOpponentSide(state.currentPlayer) : state.currentPlayer;
}

/** Walk a GameAction and add every instanceId it references to `out`.
 *  Used to scope the instance→cardId snapshot we attach to SearchReport. */
function collectInstanceIds(a: GameAction, out: Set<string>): void {
  switch (a.type) {
    case 'PLAY_CHARACTER':
    case 'ACTIVATE_ABILITY':
    case 'PLAY_ACTION':
    case 'PLAY_REACTION':
    case 'DISCARD_CARD':
      out.add(a.instanceId);
      return;
    case 'SUPPORT_CHARACTER':
      out.add(a.handInstanceId);
      out.add(a.ringInstanceId);
      return;
    case 'ASSIGN_BLOCKER':
    case 'UNASSIGN_BLOCKER':
      out.add(a.blockerInstanceId);
      return;
    case 'DECLARE_ATTACKERS':
      for (const g of a.groups) for (const id of g.attackerIds) out.add(id);
      return;
    case 'CONFIRM_BLOCKERS':
    case 'SKIP_PHASE':
    case 'ADVANCE_PHASE':
      return;
  }
}

function buildInstanceCardIdMap(
  state: GameState,
  considered: ScoredAction[],
  chosen: GameAction,
): Record<string, string> {
  const ids = new Set<string>();
  collectInstanceIds(chosen, ids);
  for (const c of considered) {
    collectInstanceIds(c.action, ids);
    for (const p of c.principalVariation) collectInstanceIds(p, ids);
  }
  const out: Record<string, string> = {};
  for (const id of ids) {
    const inst = state.cardInstances.get(id);
    if (inst && !inst.isUnknown) out[id] = inst.cardId;
  }
  return out;
}

/** Search-time action pruning. Strips moves that are structurally
 *  redundant in a tree-search context — applied at every node before
 *  the search expands children.
 *
 *  Currently drops:
 *    - UNASSIGN_BLOCKER. The action has no game-state effect on its own
 *      (combat doesn't resolve until CONFIRM_BLOCKERS), and ASSIGN_BLOCKER
 *      already replaces any prior assignment for the same group OR for
 *      the same blocker. The AI never needs to "take back" a block — it
 *      just doesn't assign that blocker in the first place, or reassigns
 *      directly. Letting the search consider UNASSIGN burns depth on
 *      block↔unblock no-op cycles.
 *    - PLAY_ACTION with a clearly no-op effect at this state — see
 *      `isNoOpPlayAction`. Each child action in the search costs one
 *      depth ply; a "free" PLAY_ACTION that doesn't change the game
 *      state lets the AI consume depth to hide future opponent
 *      pressure outside the search horizon (the "ostrich" failure
 *      mode — see Battle Hymn with no support partners). Dropping
 *      these keeps the AI honest. */
/** The hand-card instanceId a card-play action spends, or null if the action
 *  isn't a play-from-hand. Used to cap synthetic opponent plays per card. */
function cardPlayInstanceId(a: GameAction): string | null {
  return (a.type === 'PLAY_CHARACTER' || a.type === 'PLAY_ACTION' || a.type === 'PLAY_REACTION')
    ? a.instanceId
    : null;
}

function pruneSearchActions(
  legal: GameAction[],
  state: GameState,
  profile: AIProfile,
  catalog: Map<string, CardDef>,
  aiSide: PlayerSide,
): GameAction[] {
  // Explicit GameAction[] annotation prevents TS from narrowing via the
  // negative filter predicate (which would conflict with the wider
  // return type of compactRingSlotMoves).
  let result: GameAction[] = legal.filter(a => a.type !== 'UNASSIGN_BLOCKER');
  result = result.filter(a => !isNoOpPlayAction(a, state, catalog));

  // Drop strictly-dominated united attacks: a united attack only adds value
  // by piercing a blocker (excess damage spills to the opponent's HP). With
  // no eligible blocker, it deals the same total as the attackers hitting
  // individually but wastes the united spirit cost — so the AI used to make
  // pointless united attacks just to hold a character back. Prune them when
  // the defender can't block. They stay legal in getLegalActions (a human may
  // still make one); this is a search-only cut that also shrinks branching.
  const hasUnitedAttack = result.some(a => a.type === 'DECLARE_ATTACKERS' && a.groups.some(g => g.united));
  if (hasUnitedAttack && !defenderHasEligibleBlocker(state, catalog)) {
    result = result.filter(a => !(a.type === 'DECLARE_ATTACKERS' && a.groups.some(g => g.united)));
  }

  // Cap the OPPONENT's synthetic card-plays. When the opponent's hand is
  // determinized (samples >= 1) it's just a random sample of their deck, so
  // expanding every playable card is wasted branching. Keep plays for at most
  // `maxSyntheticPlays` distinct hand cards; the AI's own plays are never
  // capped. Runs before compaction, in the determinized (shuffled) hand order,
  // so different determinizations sample different subsets. Non-play actions
  // and already-kept cards pass through, so if the opponent has fewer valid
  // card-plays than the cap, all are kept (filter after valid plays are known).
  const cap = profile.search.maxSyntheticPlays;
  if (
    cap !== undefined
    && (profile.determinization?.samples ?? 0) >= 1
    && actingSide(state) !== aiSide
  ) {
    const keptCards = new Set<string>();
    result = result.filter(a => {
      const inst = cardPlayInstanceId(a);
      if (inst === null) return true;          // not a card-play
      if (keptCards.has(inst)) return true;    // another play of a kept card
      if (keptCards.size < cap) { keptCards.add(inst); return true; }
      return false;                            // over the cap → drop this card's plays
    });
  }

  if (profile.compactRingSlots) result = compactRingSlotMoves(result, state);
  return result;
}

/** Identify PLAY_ACTION moves that have no game-state effect in the
 *  current position. Each case mirrors a way a player can fire an
 *  action card and gain nothing for the spirit + card spent:
 *
 *  - `grant-extra-support` / `grant-extra-rally`: useless unless the
 *    player is at their per-turn cap AND has at least one play that
 *    the freed slot enables. A pristine slot wouldn't be consumed by
 *    the cap, so granting more is just card-economy waste.
 *  - `boost` / `armor`: targets allies; no ring characters means no
 *    target, so the buff has nowhere to land.
 *  - `slay`: targets enemies whose effective power meets the
 *    intensity; if no enemy clears the bar (or there are no enemies
 *    at all), the card resolves with no kill.
 *  - `heal`: bumping HP when already at cap is silently truncated.
 *
 *  Pruning these keeps the search's per-action depth cost honest. The
 *  AI can still play these cards when they DO accomplish something —
 *  we only drop the strictly-no-op cases. */
function isNoOpPlayAction(
  action: GameAction,
  state: GameState,
  catalog: Map<string, CardDef>,
): boolean {
  if (action.type !== 'PLAY_ACTION') return false;
  const inst = state.cardInstances.get(action.instanceId);
  if (!inst || inst.isUnknown) return false;
  const def = catalog.get(inst.cardId);
  if (!def || def.type !== 'action') return false;

  const side = state.currentPlayer;
  const ps = getPlayerState(state, side);
  const oppSide = getOpponentSide(side);
  const op = getPlayerState(state, oppSide);

  switch (def.effect) {
    case 'grant-extra-support': {
      // Only useful if (a) we're already at this turn's support cap
      // and (b) we have at least one (hand-character, ring-character)
      // pair that would actually pass canSupport's tag/freshness
      // gates given the freed slot.
      const atCap = ps.supportsUsed >= getSupportsPerTurn(state, side, catalog);
      if (!atCap) return true;
      for (const handId of ps.hand) {
        if (handId === action.instanceId) continue;
        for (const slot of ps.ring) {
          if (!slot.instanceId) continue;
          if (canSupport(state, handId, slot.instanceId, catalog, { ignoreSupportsUsed: true })) {
            return false;
          }
        }
      }
      return true;
    }
    case 'grant-extra-rally': {
      // Useful only if at the rally cap AND we hold at least one
      // hand-character that could be played into an empty ring slot
      // if the cap were lifted. Inlined rather than reusing
      // canPlayCharacter because that helper bakes in the rally cap.
      const atCap = ps.ralliesUsed >= getRalliesPerTurn(state, side, catalog);
      if (!atCap) return true;
      const ringCardIds = new Set<string>();
      let hasOpenSlot = false;
      for (const slot of ps.ring) {
        if (slot.instanceId === null) { hasOpenSlot = true; continue; }
        const r = state.cardInstances.get(slot.instanceId);
        if (r) ringCardIds.add(r.cardId);
      }
      if (!hasOpenSlot) return true;
      for (const handId of ps.hand) {
        if (handId === action.instanceId) continue;
        const hi = state.cardInstances.get(handId);
        if (!hi || hi.isUnknown) continue;
        const hd = catalog.get(hi.cardId);
        if (!hd || hd.type !== 'character') continue;
        if (ringCardIds.has(hi.cardId)) continue;            // duplicate gate
        if (hd.spirit < 0 && !canAfford(ps.spirit, -hd.spirit)) continue;
        if ((hd.clock ?? 1) > Math.max(state.clock, 1)) continue;
        return false;
      }
      return true;
    }
    case 'boost':
    case 'armor': {
      // Both target ring characters; with an empty ring there's
      // nothing to buff.
      return ps.ring.every(s => s.instanceId === null);
    }
    case 'slay': {
      // Slay kills any enemy whose effective power meets `intensity`.
      // No enemy at or above that bar = no kill.
      const threshold = def.intensity ?? 0;
      for (const slot of op.ring) {
        if (!slot.instanceId) continue;
        if (getEffectivePower(state, slot.instanceId, catalog) >= threshold) return false;
      }
      return true;
    }
    case 'heal': {
      return ps.hp >= state.maxHp;
    }
    default:
      return false;
  }
}

/** Produce one PIMC determinization: shuffle the union of the opponent's
 *  hand + deck and reassign which subset is currently "in hand" vs. "on
 *  the deck". Card identities are preserved (the AI legitimately knows
 *  what cards are in the opponent's deck) — only the hand/deck split is
 *  resampled.
 *
 *  Uses an isolated PRNG so live game randomness (next card drawn,
 *  random discards, etc.) is unaffected. */
function determinizeOpponentHand(
  state: GameState,
  opponentSide: PlayerSide,
  rng: PRNGState,
): GameState {
  const next = cloneState(state);
  const opp = getPlayerState(next, opponentSide);
  if (opp.hand.length === 0 && opp.deck.length === 0) return next;
  const pool = [...opp.hand, ...opp.deck];
  const shuffled = shuffleWith(rng, pool);
  const handSize = opp.hand.length;
  opp.hand = shuffled.slice(0, handSize);
  opp.deck = shuffled.slice(handSize);
  for (let i = 0; i < shuffled.length; i++) {
    const inst = next.cardInstances.get(shuffled[i]);
    if (!inst) continue;
    inst.zone = i < handSize ? 'hand' : 'deck';
  }
  return next;
}

/** Collapse PLAY_CHARACTER actions to one slot per candidate. The slot is
 *  picked from a hash of the state's PRNG position + the instance id, so:
 *    - The choice doesn't consume an rng draw (live game flow unaffected).
 *    - Different cards routinely pick different slots (visual variety).
 *    - The same card across turns can shift slots as rng.state advances.
 *    - Same seed + same actions → same picks (replay-deterministic). */
function compactRingSlotMoves(legal: GameAction[], state: GameState): GameAction[] {
  const grouped = new Map<string, Array<GameAction & { type: 'PLAY_CHARACTER' }>>();
  const others: GameAction[] = [];
  for (const a of legal) {
    if (a.type === 'PLAY_CHARACTER') {
      const list = grouped.get(a.instanceId);
      if (list) list.push(a);
      else grouped.set(a.instanceId, [a]);
    } else {
      others.push(a);
    }
  }
  if (grouped.size === 0) return legal;
  const picked: GameAction[] = [];
  for (const [instId, options] of grouped) {
    if (options.length === 1) {
      picked.push(options[0]);
      continue;
    }
    // FNV-1a seeded by rng.state, mixed with the instance id. Cheap, no
    // dependency on the live PRNG state machine.
    let hash = state.rng.state >>> 0;
    for (let i = 0; i < instId.length; i++) {
      hash = Math.imul(hash ^ instId.charCodeAt(i), 0x01000193) >>> 0;
    }
    picked.push(options[hash % options.length]);
  }
  return [...picked, ...others];
}

interface MinimaxResult {
  score: number;
  pv: GameAction[];
  nodes: number;
}

/** Time control for iterative deepening. When present, minimax checks
 *  the wall-clock deadline every ~1024 node visits and throws
 *  SEARCH_TIMEOUT once it passes, so the in-progress (over-budget)
 *  depth can be unwound and discarded — the deepest fully-completed
 *  depth's result is used instead. Absent (fixed-depth path) = no
 *  checks, no overhead. */
interface TimeControl {
  deadline: number;   // performance.now() ms
  counter: number;    // node visits, for throttled deadline checks
}
const SEARCH_TIMEOUT = Symbol('search-timeout');

/** Enumerate all size-k subsets of `items` (order not preserved beyond
 *  the ascending-index canonical form). Used by the cleanup-discard
 *  batching path to iterate over C(N, k) sets instead of the M-nested
 *  linear tree that would otherwise explore all N!/(N-k)! orderings. */
function combinations<T>(items: T[], k: number): T[][] {
  const out: T[][] = [];
  const current: T[] = [];
  const pick = (start: number): void => {
    if (current.length === k) { out.push([...current]); return; }
    const need = k - current.length;
    for (let i = start; i <= items.length - need; i++) {
      current.push(items[i]);
      pick(i + 1);
      current.pop();
    }
  };
  pick(0);
  return out;
}

/** Alternative expansion for a cleanup-phase node when M ≥ 2 discards
 *  are still required. Cleanup DISCARD_CARDs commute (order changes
 *  nothing about the resulting state), so the default M-ply linear
 *  search wastes tree budget exploring M!/(M-k)! orderings of the
 *  same C(N, M) subsets. This helper collapses the whole discard
 *  decision into ONE ply: enumerate every size-M subset of the hand,
 *  apply the whole batch atomically via `replayActions`, and recurse
 *  once. Same alpha-beta bookkeeping as the normal loop, so a proven
 *  cutoff still short-circuits. */
function expandCleanupBatch(
  state: GameState,
  catalog: Map<string, CardDef>,
  aiSide: PlayerSide,
  profile: AIProfile,
  depth: number,
  alpha: number,
  beta: number,
  tc: TimeControl | undefined,
  legal: GameAction[],
  batchSize: number,
): MinimaxResult {
  const isMax = actingSide(state) === aiSide;
  const handIds = legal.map(a => (a as GameAction & { type: 'DISCARD_CARD' }).instanceId);
  const combos = combinations(handIds, batchSize);
  // Spend one depth ply *per discard in the batch* so post-cleanup
  // recurses at the same depth as the unbatched linear expansion
  // would have reached (which spent 1 ply per DISCARD_CARD). Without
  // this the batch would deepen post-cleanup by M-1 plies, inflating
  // total tree size and defeating the point of batching. Caller
  // already clamps `batchSize <= depth`, so `branchDepth >= 0`.
  //
  // freeCleanupDiscard: forced discards don't consume lookahead at all, so
  // the rest of the turn is reached at the same depth whether or not the
  // hand was over size (see AIProfile.search.freeCleanupDiscard).
  const branchDepth = profile.search.freeCleanupDiscard ? depth : depth - batchSize;

  let bestScore = isMax ? -Infinity : Infinity;
  let bestPV: GameAction[] = [];
  let totalNodes = 1;

  for (const combo of combos) {
    if (tc !== undefined && (++tc.counter & 1023) === 0 && performance.now() >= tc.deadline) {
      throw SEARCH_TIMEOUT;
    }
    const batch: GameAction[] = combo.map(id => ({ type: 'DISCARD_CARD', instanceId: id } as const));
    const result = replayActions(state, batch, catalog);
    const child = minimax(result.state, catalog, aiSide, profile, branchDepth, alpha, beta, tc);
    totalNodes += child.nodes;
    const childPV: GameAction[] = [...batch, ...child.pv];
    if (isMax) {
      if (child.score > bestScore) { bestScore = child.score; bestPV = childPV; }
      if (bestScore > alpha) alpha = bestScore;
    } else {
      if (child.score < bestScore) { bestScore = child.score; bestPV = childPV; }
      if (bestScore < beta) beta = bestScore;
    }
    if (alpha >= beta) break;
  }
  return { score: bestScore, pv: bestPV, nodes: totalNodes };
}

function minimax(
  state: GameState,
  catalog: Map<string, CardDef>,
  aiSide: PlayerSide,
  profile: AIProfile,
  depth: number,
  alpha: number,
  beta: number,
  tc?: TimeControl,
): MinimaxResult {
  if (tc !== undefined && (++tc.counter & 1023) === 0 && performance.now() >= tc.deadline) {
    throw SEARCH_TIMEOUT;
  }
  if (depth === 0 || state.winner !== null) {
    return { score: evaluate(state, aiSide, profile.weights, catalog, depth), pv: [], nodes: 1 };
  }

  const legal = pruneSearchActions(getLegalActions(state, catalog), state, profile, catalog, aiSide);
  if (legal.length === 0) {
    return { score: evaluate(state, aiSide, profile.weights, catalog, depth), pv: [], nodes: 1 };
  }
  // Forced move: with only one option there's no decision to score.
  // Apply it and recurse without decrementing depth so the budget stays
  // reserved for nodes that actually branch. Terminates naturally
  // because every action consumes game progression — a chain of forced
  // moves can't loop forever.
  if (legal.length === 1) {
    const action = legal[0];
    const result = replayActions(state, [action], catalog);
    const child = minimax(result.state, catalog, aiSide, profile, depth, alpha, beta, tc);
    return { score: child.score, pv: [action, ...child.pv], nodes: child.nodes + 1 };
  }

  // Determinized opponent's cleanup discard: which synthetic cards they pitch
  // is arbitrary (their hand is a random sample of their deck, not their real
  // cards), so modeling *which* to discard is noise. Discard `need` cards
  // arbitrarily as one batch and recurse once — collapsing C(N, k) subsets
  // (or the N single-discard branches) to 1, with no quality cost. The AI's
  // own real discards fall through to the full enumeration below.
  if (
    profile.search.arbitrarySyntheticDiscard &&
    state.phase === 'cleanup' &&
    legal.every(a => a.type === 'DISCARD_CARD') &&
    (profile.determinization?.samples ?? 0) >= 1 &&
    actingSide(state) !== aiSide
  ) {
    const need = mustDiscard(state, state.currentPlayer, catalog);
    // Free under freeCleanupDiscard; else spend one ply per discard (matching
    // the batched real-discard path), clamped to the depth budget.
    const batchSize = profile.search.freeCleanupDiscard ? need : Math.min(need, depth);
    const batch = legal.slice(0, batchSize);
    const branchDepth = profile.search.freeCleanupDiscard ? depth : depth - batchSize;
    const result = replayActions(state, batch, catalog);
    const child = minimax(result.state, catalog, aiSide, profile, branchDepth, alpha, beta, tc);
    return { score: child.score, pv: [...batch, ...child.pv], nodes: child.nodes + 1 };
  }

  // Cleanup-phase batching: several sequential DISCARD_CARDs commute
  // (the resulting hand + discardPile don't depend on the order), so
  // exploring all M! orderings of picking M cards is wasted budget.
  // Collapse to a single ply that iterates C(N, M) subsets — big win
  // when the opponent's just cast Ancient Wisdom + Odysseus and now
  // faces 3+ cleanup discards. Only kicks in when M >= 2; a lone
  // discard has no ordering redundancy to eliminate.
  if (
    state.phase === 'cleanup' &&
    legal.every(a => a.type === 'DISCARD_CARD')
  ) {
    const need = mustDiscard(state, state.currentPlayer, catalog);
    // Clamp the batch to the remaining depth budget. When
    // depth < need we still batch — but only pick a size-`depth`
    // subset, leaving the leftover discards for the next call.
    // The alternative (falling back to linear expansion) would
    // explore N × (N-1) × ... × (N-depth+1) permutations of the
    // same subsets; batching cuts that to C(N, depth), which is
    // a factor of `depth!` reduction. A batch of 5 discards vs
    // a linear 5-ply tree is ~120× fewer branches, and the
    // resulting decision quality is identical since the trailing
    // discard is a follow-up call anyway.
    const batchSize = Math.min(need, depth);
    if (batchSize >= 2 && need <= legal.length) {
      return expandCleanupBatch(state, catalog, aiSide, profile, depth, alpha, beta, tc, legal, batchSize);
    }
  }

  const isMax = actingSide(state) === aiSide;
  let bestScore = isMax ? -Infinity : Infinity;
  let bestPV: GameAction[] = [];
  let totalNodes = 1;

  // Combat resolution (the `counter` phase: block/reaction/confirm in
  // response to an attack) is part of the attacking move, not a separate
  // lookahead ply. When `freeCounterPhase` is on, recurse through it
  // without spending depth, so attack lines reach the same horizon as
  // passing instead of being penalized ~1 ply by the forced block decision
  // (which otherwise makes the AI pathologically passive). See
  // AIProfile.search.freeCounterPhase.
  // Some phases are searched "for free" — their moves don't consume a ply —
  // so sibling lines are compared at the same lookahead horizon. Combat
  // resolution (counter) is part of the attacking move; forced cleanup
  // discards are bookkeeping, not tempo. See AIProfile.search flags.
  const freePhase =
    (profile.search.freeCounterPhase && state.phase === 'counter') ||
    (profile.search.freeCleanupDiscard && state.phase === 'cleanup');
  const branchDepth = freePhase ? depth : depth - 1;

  for (const action of legal) {
    const result = replayActions(state, [action], catalog);
    const child = minimax(result.state, catalog, aiSide, profile, branchDepth, alpha, beta, tc);
    totalNodes += child.nodes;
    const childPV: GameAction[] = [action, ...child.pv];

    if (isMax) {
      if (child.score > bestScore) {
        bestScore = child.score;
        bestPV = childPV;
      }
      if (bestScore > alpha) alpha = bestScore;
    } else {
      if (child.score < bestScore) {
        bestScore = child.score;
        bestPV = childPV;
      }
      if (bestScore < beta) beta = bestScore;
    }
    if (alpha >= beta) break;  // α-β cutoff
  }

  return { score: bestScore, pv: bestPV, nodes: totalNodes };
}

/** Per-call yielding controls. `frameBudgetMs` caps how long the
 *  root iteration runs synchronously before yielding back to the
 *  event loop; omitted / undefined = no yielding (synchronous run,
 *  intended for Node tools). The browser passes a small value
 *  (~10-16ms) so a slow search doesn't visibly hitch the UI between
 *  AI turns. */
export interface SearchProgress {
  /** Root actions fully scored so far in the current deepening pass. */
  rootDone: number;
  /** Total root actions being scored. */
  rootTotal: number;
  /** Depth of the pass in progress (== maxDepth for fixed-depth profiles). */
  depth: number;
  /** Cumulative nodes explored so far this call. */
  nodes: number;
}

export interface SearchOptions {
  frameBudgetMs?: number;
  /** Optional progress sink, called synchronously as the search advances
   *  (after each root action completes). Used by the Web Worker to drive a
   *  "Thinking…" progress bar. No effect on the result — the search is
   *  deterministic regardless. Node tools omit it. */
  onProgress?: (p: SearchProgress) => void;
}

/** Yield to the event loop. setTimeout(0) is throttled to ~4ms in
 *  browsers and ~1ms in Node, which is fine here: we yield whenever
 *  a synchronous slice has run past `frameBudgetMs`, not on every
 *  iteration. */
function yieldToEventLoop(): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, 0));
}

export async function getSearchAction(
  state: GameState,
  catalog: Map<string, CardDef>,
  side: PlayerSide,
  profile: AIProfile,
  options?: SearchOptions,
): Promise<GameAction | null> {
  return (await getSearchActionWithReport(state, catalog, side, profile, options)).action;
}

export async function getSearchActionWithReport(
  state: GameState,
  catalog: Map<string, CardDef>,
  side: PlayerSide,
  profile: AIProfile,
  options?: SearchOptions,
): Promise<{ action: GameAction | null; report: SearchReport | null }> {
  const startTime = Date.now();
  const legal = pruneSearchActions(getLegalActions(state, catalog), state, profile, catalog, side);
  if (legal.length === 0) {
    return { action: null, report: null };
  }

  // Forced move at the root — only one legal action means no decision to
  // make. Skip the tree entirely; the report shows a single considered
  // option so the panel can display "(forced)" implicitly via the count.
  if (legal.length === 1) {
    const action = legal[0];
    const considered: ScoredAction[] = [{ action, score: 0, principalVariation: [action] }];
    return {
      action,
      report: {
        chosenAction: action,
        rootScore: 0,
        consideredActions: considered,
        searchDepth: 0,
        nodesExplored: 0,
        searchTimeMs: Date.now() - startTime,
        profileId: profile.id,
        instanceCardIds: buildInstanceCardIdMap(state, considered, action),
      },
    };
  }

  // Epsilon weakening: with probability ε, ignore the search and pick a
  // random legal action. Uses the seeded PRNG so the run stays replayable.
  if (profile.epsilon > 0 && nextFloat(state.rng) < profile.epsilon) {
    const pick = legal[nextInt(state.rng, legal.length)];
    const considered: ScoredAction[] = legal.map(a => ({ action: a, score: 0, principalVariation: [a] }));
    return {
      action: pick,
      report: {
        chosenAction: pick,
        rootScore: 0,
        consideredActions: considered,
        searchDepth: 0,
        nodesExplored: 1,
        searchTimeMs: 0,
        profileId: profile.id,
        instanceCardIds: buildInstanceCardIdMap(state, considered, pick),
      },
    };
  }

  // Determinization setup. Three regimes:
  //   - samples=0: perfect info, single pass over live state with root
  //     α-β (cheapest, the AI "sees" the opponent's actual hand).
  //   - samples=1: one determinization, single pass with α-β off (the
  //     AI plays under uncertainty but doesn't average).
  //   - samples≥2: N determinizations with per-action averaging, α-β
  //     off at the root.
  //
  // `iterations` is the actual number of passes (1 for samples=0,
  // otherwise samples). Determinized state is only built when samples≥1.
  const samples = Math.max(0, profile.determinization?.samples ?? 0);
  const iterations = Math.max(1, samples);
  const opponentSide = getOpponentSide(side);
  // Private PRNG for sample shuffling — must not advance the live
  // state's stream (would corrupt future card draws and break replay
  // determinism for the live game).
  const detRng = clonePRNG(state.rng);

  // Score below which we treat a sample's outcome as a forced terminal
  // loss. Eval magnitudes for non-terminal positions never come close to
  // this (a few thousand at most); only terminal scores or α-β-bounded
  // returns from a fully-losing subtree land here.
  const TERMINAL_LOSS_THRESHOLD = -5e8;

  // Root α-β requires (a) perfect info and (b) no per-action averaging,
  // so it's only safe when samples=0. With any determinization (samples
  // ≥ 1) we disable it: pruning returns upper bounds instead of exact
  // scores, which is fine for picking a winner in a single pass but
  // wrong for averaging across passes. We keep α-β inside per-action
  // minimax recursion — that's a one-action subtree, not aggregating.
  const useRootAlphaBeta = samples === 0;

  // Cooperative yield deadline. `frameBudgetMs` undefined → no
  // yielding (run synchronous, the Node-tool default). Otherwise we
  // run minimax for one root-action subtree at a time and yield
  // whenever we've burned through the per-frame budget, keeping the
  // browser responsive on slow positions.
  const frameBudgetMs = options?.frameBudgetMs;
  const onProgress = options?.onProgress;
  let yieldDeadline = frameBudgetMs !== undefined ? performance.now() + frameBudgetMs : Infinity;

  // Pre-sample the determinization worlds ONCE so every depth (in the
  // iterative-deepening path) scores against the same hypothetical
  // opponent hands — re-rolling per depth would make the deepest
  // completed depth's pick noisy. For samples=0 the single "world" is
  // the live state. detRng is drawn in the same order the old inline
  // loop used, so the fixed-depth path stays byte-identical.
  const worlds: GameState[] = [];
  for (let s = 0; s < iterations; s++) {
    worlds.push(samples === 0 ? state : determinizeOpponentHand(state, opponentSide, detRng));
  }

  // Iterative deepening is opt-in via `search.timeBudgetMs`. Unset → a
  // single fixed-depth pass at maxDepth (the original behavior,
  // preserved verbatim for A/B comparison). Set → deepen from 1 ply up
  // to maxDepth, keeping the deepest pass that FINISHED before the
  // wall-clock deadline. An over-budget pass throws SEARCH_TIMEOUT and
  // is discarded. The shallowest pass never carries a deadline, so even
  // a tiny budget yields a usable result.
  const idEnabled = profile.search.timeBudgetMs !== undefined;
  const maxDepth = profile.search.maxDepth;
  const startDepth = idEnabled ? 1 : maxDepth;
  const deadline = idEnabled ? performance.now() + profile.search.timeBudgetMs! : Infinity;

  // Committed accumulators from the deepest fully-completed depth.
  let scoreSums: number[] = new Array(legal.length).fill(0);
  let sampleTerminalLoss: boolean[][] = legal.map(() => new Array(iterations).fill(false));
  let firstPvs: GameAction[][] = legal.map(a => [a]);
  let completedDepth = startDepth;
  let totalNodes = 0;
  // How many determinization samples the committed pass actually ran — equals
  // `iterations` unless the sampleNodeBudget cutoff stopped it early. All
  // averaging / per-sample logic must use THIS, not `iterations`.
  let samplesCompleted = iterations;
  let sampleNodes: number[] = [];   // cumulative totalNodes after each completed sample
  const sampleNodeBudget = profile.search.sampleNodeBudget;

  for (let depth = startDepth; depth <= maxDepth; depth++) {
    const passScoreSums = new Array<number>(legal.length).fill(0);
    const passTerminalLoss = legal.map(() => new Array<boolean>(iterations).fill(false));
    const passFirstPvs: GameAction[][] = legal.map(a => [a]);
    // The shallowest pass runs without a deadline so we always commit
    // SOMETHING; deeper passes carry a TimeControl and may be cut off.
    const tc: TimeControl | undefined = (idEnabled && depth > startDepth)
      ? { deadline, counter: 0 }
      : undefined;
    let timedOut = false;
    let passSamplesRun = 0;
    const passSampleNodes: number[] = [];

    try {
      for (let s = 0; s < iterations; s++) {
        const sampleState = worlds[s];
        let rootAlpha = -Infinity;
        const rootBeta = Infinity;
        for (let i = 0; i < legal.length; i++) {
          const action = legal[i];
          const result = replayActions(sampleState, [action], catalog);
          let score: number;
          let pv: GameAction[];
          if (result.state.winner !== null) {
            // One root action consumed → `depth - 1` plies remain at this
            // terminal. Used by evaluate() to prefer wins-sooner / losses-later.
            score = evaluate(result.state, side, profile.weights, catalog, depth - 1);
            pv = [action];
            totalNodes++;
          } else {
            const child = minimax(
              result.state, catalog, side, profile,
              depth - 1,
              useRootAlphaBeta ? rootAlpha : -Infinity,
              rootBeta,
              tc,
            );
            score = child.score;
            pv = [action, ...child.pv];
            totalNodes += child.nodes;
          }
          passScoreSums[i] += score;
          passTerminalLoss[i][s] = score < TERMINAL_LOSS_THRESHOLD;
          if (s === 0) passFirstPvs[i] = pv;
          if (useRootAlphaBeta && score > rootAlpha) rootAlpha = score;

          // Yield between root-action subtrees if we've used up the
          // frame budget. Each subtree runs sync; only the gaps between
          // them go through the event loop.
          if (frameBudgetMs !== undefined && performance.now() >= yieldDeadline) {
            await yieldToEventLoop();
            yieldDeadline = performance.now() + frameBudgetMs;
          }

          onProgress?.({ rootDone: i + 1, rootTotal: legal.length, depth, nodes: totalNodes });
        }
        passSamplesRun = s + 1;
        passSampleNodes.push(totalNodes);
        // Adaptive-samples cutoff: after a sample completes, stop starting new
        // ones if we've spent the node budget. Always keeps ≥1 sample, and the
        // in-progress sample already finished. Node-count based → replayable.
        if (sampleNodeBudget !== undefined && totalNodes >= sampleNodeBudget && s + 1 < iterations) {
          break;
        }
      }
    } catch (e) {
      if (e === SEARCH_TIMEOUT) timedOut = true;
      else throw e;
    }

    if (!timedOut) {
      scoreSums = passScoreSums;
      sampleTerminalLoss = passTerminalLoss;
      firstPvs = passFirstPvs;
      completedDepth = depth;
      samplesCompleted = passSamplesRun;
      sampleNodes = passSampleNodes;
    }
    // Stop deepening once the budget is spent (or this pass was cut off).
    if (timedOut || performance.now() >= deadline) break;
  }

  // Doom-stall rule (attack-phase only): the AI's fixed-depth search
  // can't see far enough ahead to recognize slow inevitable losses
  // (mill, on-board persistent damage). When the board is locked up
  // (ring full, no plays available, opponent isn't pressuring) and the
  // AI is on track to lose at the current passive rate, attacking is
  // the only chance to break the loop — even if the immediate trade
  // doesn't show a profit within depth. Penalize SKIP_PHASE here so
  // the search prefers any non-losing attack. We apply the penalty per
  // sample so it survives the averaging.
  //
  // The penalty is large enough to dominate normal eval magnitudes (a
  // few thousand at most) but well below TERMINAL_LOSS_THRESHOLD, so a
  // suicide attack that leads to certain loss still beats it and the
  // AI won't make a worse play just to avoid skipping.
  const DOOM_STALL_PENALTY_PER_SAMPLE = 50000;
  if (state.phase === 'attack' && actingSide(state) === side) {
    const aiState = getPlayerState(state, side);
    const opSide = getOpponentSide(side);
    const opState = getPlayerState(state, opSide);

    // (a) all ring slots full — no room for new plays
    const ringFull = aiState.ring.every(s => s.instanceId !== null);
    // (b) opponent didn't attack last turn — no immediate combat pressure
    const opponentDidntAttackLastTurn = !opState.attackedDuringTurn;
    // (c) AI hasn't played or supported this turn — board state is static
    const aiIdleThisTurn = aiState.ralliesUsed === 0 && aiState.supportsUsed === 0;

    // Doom forecast: at the current per-cycle HP / deck loss rate, how
    // many cycles until each player loses? Negative delta = bleeding;
    // positive or zero = stable or recovering (Infinity = never loses
    // at this rate). Player is doomed if their forecast loss turn is
    // strictly sooner than the opponent's.
    const forecast = (hp: number, deckSize: number, hpDelta: number, deckDelta: number): number => {
      const hpTurns = hpDelta < 0 ? hp / -hpDelta : Infinity;
      const deckTurns = deckDelta < 0 ? deckSize / -deckDelta : Infinity;
      return Math.min(hpTurns, deckTurns);
    };
    const aiForecast = forecast(aiState.hp, aiState.deck.length, aiState.lastTurnHpDelta, aiState.lastTurnDeckDelta);
    const opForecast = forecast(opState.hp, opState.deck.length, opState.lastTurnHpDelta, opState.lastTurnDeckDelta);
    const aiDoomed = aiForecast < opForecast;

    if (ringFull && opponentDidntAttackLastTurn && aiIdleThisTurn && aiDoomed) {
      const skipIdx = legal.findIndex(a => a.type === 'SKIP_PHASE');
      if (skipIdx >= 0) {
        scoreSums[skipIdx] -= DOOM_STALL_PENALTY_PER_SAMPLE * samplesCompleted;
      }
    }
  }

  // Average scores and pick the action with the highest average.
  let bestScore = -Infinity;
  let bestAction: GameAction = legal[0];
  for (let i = 0; i < legal.length; i++) {
    const avg = scoreSums[i] / samplesCompleted;
    if (avg > bestScore) {
      bestScore = avg;
      bestAction = legal[i];
    }
  }

  // Skip-aversion: with small sample counts, averaging lets SKIP_PHASE
  // win on variance alone — one lucky sampled world where SKIP didn't
  // terminate vs two where plays did. "AI passes its turn while losing"
  // reads as broken to a watching player even when statistically
  // optimal. The override is narrow on purpose — we only want to force a
  // character play, not a support or action card, because those don't
  // add board presence and feel like the AI is grasping. Conditions:
  //   1. The picked action is SKIP_PHASE
  //   2. The AI hasn't played a character this turn (ralliesUsed === 0)
  //      — if they already played, deferring is reasonable.
  //   3. SKIP_PHASE itself has at least one terminal-loss sample
  //      — i.e., the position is bad enough that even SKIP isn't safe.
  //   4. At least one PLAY_CHARACTER has a non-terminal-loss sample
  //      — there's an actual fighting-chance play available.
  if (bestAction.type === 'SKIP_PHASE') {
    const aiState = getPlayerState(state, side);
    const skipIdx = legal.findIndex(a => a.type === 'SKIP_PHASE');
    // Only consider samples that actually ran (trailing entries are default
    // false and would misread as "viable" under .some(t => !t)).
    const skipHasLoss = skipIdx >= 0 && sampleTerminalLoss[skipIdx].slice(0, samplesCompleted).some(t => t);
    if (aiState.ralliesUsed === 0 && skipHasLoss) {
      let bestPlayScore = -Infinity;
      let bestPlayAction: GameAction | null = null;
      for (let i = 0; i < legal.length; i++) {
        if (legal[i].type !== 'PLAY_CHARACTER') continue;
        const hasViable = sampleTerminalLoss[i].slice(0, samplesCompleted).some(t => !t);
        if (!hasViable) continue;
        const avg = scoreSums[i] / samplesCompleted;
        if (avg > bestPlayScore) {
          bestPlayScore = avg;
          bestPlayAction = legal[i];
        }
      }
      if (bestPlayAction) {
        bestScore = bestPlayScore;
        bestAction = bestPlayAction;
      }
    }
  }
  const considered: ScoredAction[] = legal.map((a, i) => ({
    action: a,
    score: scoreSums[i] / samplesCompleted,
    principalVariation: firstPvs[i],
  }));
  considered.sort((a, b) => b.score - a.score);

  return {
    action: bestAction,
    report: {
      chosenAction: bestAction,
      rootScore: bestScore,
      consideredActions: considered,
      // Deepest fully-completed depth — equals maxDepth in fixed-depth
      // mode, but in iterative-deepening mode reveals how far the time
      // budget actually let the search reach this position.
      searchDepth: completedDepth,
      nodesExplored: totalNodes,
      searchTimeMs: Date.now() - startTime,
      profileId: profile.id,
      instanceCardIds: buildInstanceCardIdMap(state, considered, bestAction),
      sampleNodes,
    },
  };
}
